/**
 * AuditedConsentCard — polls + outbound-care opt-in.
 *
 * Single source of truth for the parent-facing communication preferences,
 * rendered inside the /settings → Notifications tab (and reachable via the
 * /settings/notifications redirect).
 *
 * The audited SMS and marketing-email opt-in toggles (and the FCC/TCPA
 * re-confirmation modal) were removed under D-7 (#642): each recorded consent
 * for a sender that does not exist (sms.js is MFA-only; nothing reads
 * email_marketing_opted_in), so the switch persisted a value nothing
 * enforced. Each flow is rebuilt WITH its first sender — the disclosure
 * wiring is recoverable from git. The phone-side STOP/HELP webhook is
 * unaffected and always available.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, BarChart2, Heart, Calendar } from 'lucide-react';
import { api } from '@/services/api';
import { useToast } from '@/components/ui/use-toast';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { serverMessage as apiReason } from '@/lib/serverMessage';

// PATCH Outbound Care (Phase 0) — parent-facing proactive-outreach categories.
// Preferences (not TCPA consents): toggling persists immediately, no modal.
// Keys mirror the backend `config/outboundConsent.js` OUTBOUND_CONSENT_CATEGORIES.
const OUTBOUND_CARE_CATEGORIES = [
  {
    key: 'quiet_checkin',
    labelKey: 'outreach.quietCheckin.label',
    descriptionKey: 'outreach.quietCheckin.description',
    icon: Heart,
  },
  {
    key: 'anticipatory_support',
    labelKey: 'outreach.anticipatorySupport.label',
    descriptionKey: 'outreach.anticipatorySupport.description',
    icon: Calendar,
  },
] as const;

// #2109 R3 L7: these are preferences, not consent sentences (the TCPA opt-ins
// left under D-7, #642), so every string here follows the reader's language
// through the `consent` namespace. A server sentence reaches a toast only in
// English (R3-B11).
export function AuditedConsentCard() {
  const { t, i18n } = useTranslation(['consent', 'common']);
  const { toast } = useToast();
  // R3-B11: the server's English sentence (or the thrown Error's) for an
  // English reader, exactly as before; a localized generic line otherwise.
  // Not `localizedApiErrorMessage`: that reads an axios body, and these two
  // sites show `res.message` and `Error.message`.
  const english = (i18n.language ?? 'en').toLowerCase().split('-')[0] === 'en';
  const serverMessage = (message: string | undefined, fallback: string) =>
    english ? message ?? fallback : t('common:states.pleaseTryAgain');

  // Polls default ON — absence of the field means opted-in.
  const [polls, setPolls] = useState(true);
  const [outbound, setOutbound] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.consent
      .get()
      .then((r) => {
        if (r.status === 'success' && r.data) {
          // Polls: default true when field is absent (opted-in by default).
          const notifPrefs = (r.data as Record<string, unknown>).notification_prefs as
            | { polls?: boolean }
            | undefined;
          setPolls(notifPrefs?.polls !== false);
          setOutbound(r.data.outbound_consent ?? {});
        }
      })
      .catch(() => {
        toast({
          title: t('outreach.loadFailed'),
          description: t('outreach.loadFailedDescription'),
          variant: 'destructive',
        });
      })
      .finally(() => setLoading(false));
  }, [toast, t]);

  // Polls is a plain preference: persist immediately in both directions and
  // revert on failure (destructive toast — never a silent catch, B-014/048/051).
  async function persistPolls(next: boolean) {
    const prev = polls;
    setPolls(next);
    setSaving(true);
    try {
      const res = await api.consent.update({ notification_prefs_polls: next });
      if (res.status === 'success') {
        toast({ title: t('outreach.saved'), description: t('outreach.pollsSavedDescription') });
      } else {
        setPolls(prev);
        toast({
          title: t('outreach.updateFailed'),
          description: serverMessage(res.message, t('outreach.unknownError')),
          variant: 'destructive',
        });
      }
    } catch (e) {
      setPolls(prev);
      toast({
        title: t('outreach.updateFailed'),
        description: serverMessage(apiReason(e, '') || undefined, t('outreach.updateFailedDescription')),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  }

  // Outbound-care categories are preferences (not TCPA): persist immediately in
  // both directions via the audited notifications endpoint; revert on failure
  // (surface a destructive toast — never a silent catch, per B-014/048/051).
  async function persistOutbound(category: string, next: boolean) {
    const prev = outbound[category] ?? false;
    setOutbound((o) => ({ ...o, [category]: next }));
    setSaving(true);
    try {
      const res = await api.notifications.update({ outbound_consent: { [category]: next } });
      if (res.status === 'success') {
        toast({ title: t('outreach.saved'), description: t('outreach.outreachSavedDescription') });
      } else {
        setOutbound((o) => ({ ...o, [category]: prev }));
        toast({
          title: t('outreach.updateFailed'),
          description: serverMessage(res.message, t('outreach.unknownError')),
          variant: 'destructive',
        });
      }
    } catch (e) {
      setOutbound((o) => ({ ...o, [category]: prev }));
      toast({
        title: t('outreach.updateFailed'),
        description: serverMessage(apiReason(e, '') || undefined, t('outreach.updateFailedDescription')),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-primary" />
          <CardTitle>{t('outreach.title')}</CardTitle>
        </div>
        <CardDescription>
          {t('outreach.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-0.5">
            <div className="flex items-center gap-2 text-body font-medium">
              <BarChart2 className="h-4 w-4 text-stevens-ink-3" />
              {t('outreach.polls.label')}
            </div>
            <p className="text-body-sm text-stevens-ink-3">
              {t('outreach.polls.description')}
            </p>
          </div>
          <Switch
            aria-label={t('outreach.polls.ariaLabel')}
            checked={polls}
            disabled={loading || saving}
            onCheckedChange={(v) => void persistPolls(v)}
          />
        </div>

        {OUTBOUND_CARE_CATEGORIES.map(({ key, labelKey, descriptionKey, icon: Icon }) => (
          <div key={key} className="flex items-start justify-between gap-4">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2 text-body font-medium">
                <Icon className="h-4 w-4 text-stevens-ink-3" />
                {t(labelKey)}
              </div>
              <p className="text-body-sm text-stevens-ink-3">{t(descriptionKey)}</p>
            </div>
            <Switch
              aria-label={t(labelKey)}
              checked={outbound[key] ?? false}
              disabled={loading || saving}
              onCheckedChange={(v) => persistOutbound(key, v)}
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
